import {gql} from '@apollo/client';
import {noop} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import React, {useEffect, useCallback, useRef} from 'react';

import DataConnector from 'connect/DataConnector';
import {Status as AutomationFileStatusGraphQL} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {InstrumentServiceDetails} from 'instruments/connections/ConnectionConstants';
import {EditorPropertiesContext} from 'notebook/notes/core-ui/NoteContext';
import Col from 'ui-library/bs/Col';
import Row from 'ui-library/bs/Row';
import {ButtonStyle} from 'ui-library/buttons/Button';
import Chip, {ChipSize} from 'ui-library/chips/Chip';
import * as Colors from 'ui-library/Colors';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

import {AutomationInputFileGraphQL} from '../../../graphql-fragments/lab-automation/__generated/AutomationInputFileGraphQL';
import {AutomationOutputFileGraphQL} from '../../../graphql-fragments/lab-automation/__generated/AutomationOutputFileGraphQL';
import {
  RunAutomationInputFileViewerQuery,
  RunAutomationInputFileViewerQueryVariables,
} from './__generated/RunAutomationInputFileViewerQuery';
import {setAutomationFileStatus} from './AutomationFileActions';
import * as AutomationFileDAO from './AutomationFileDAO';
import {runAutomationInputFileStatusModal} from './AutomationFileStatusModal';
import FailedRunAutomationFileButton from './FailedRunAutomationFileButton';
import GenerateAutomationInputFileButton from './GenerateAutomationInputFileButton';
import getFinalInputFileBlob from './getFinalInputFileBlob';
import getStatusForInputFileViewer from './getStatusForInputFileViewer';
import SucceededAutomationFileButton from './SucceededAutomationFileButton';

export const RUN_AUTOMATION_INPUT_FILE_VIEWER_QUERY = gql`
  query RunAutomationInputFileViewerQuery($automationInputFileId: StrictString!)
  @skipAudit
  @engComponent(name: LAB_AUTOMATION) {
    automationInputFile(id: $automationInputFileId) {
      ... on AutomationInputFileNode {
        id
        status
        transformFiles {
          id
          status
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

interface SucceededAutomationInputFileProps {
  runId: string | null;
  automationInputFile: AutomationInputFileGraphQL;
  isStale: boolean;
  locked: boolean;
  handleRefreshVersionedData: () => Promise<void>;
  updateAutomationInputFile: (automationInputFile: AutomationInputFileGraphQL) => Promise<void>;
}

function SucceededAutomationInputFile(props: SucceededAutomationInputFileProps): JSX.Element {
  const includeChip =
    getStatusForInputFileViewer(props.automationInputFile) === AutomationFileStatusGraphQL.succeeded &&
    !props.isStale;
  const blob = getFinalInputFileBlob(props.automationInputFile);
  const editorPropertiesContext = EditorPropertiesContext.useContext();
  return (
    <>
      <SucceededAutomationFileButton
        buttonStyle={ButtonStyle.TERTIARY_GRAY}
        buttonText="Complete"
        buttonIcon={<BenchlingIcon className={css(styles.buttonIcon)} icon="check" />}
        isStale={props.isStale}
        locked={props.locked}
        handleRefreshVersionedData={props.handleRefreshVersionedData}
        onClick={() => {
          runAutomationInputFileStatusModal({
            automationFile: props.automationInputFile,
            runId: props.runId,
            locked: props.locked,
            updateAutomationInputFile: props.updateAutomationInputFile,
            editorPropertiesContext,
          });
        }}
        dataTestAttrs={{component: 'RunAutomationInputFileViewer', element: 'completeFileButton'}}
      />
      {includeChip ? (
        <Chip
          className="chip-container"
          icon="paperclip"
          size={ChipSize.MINI}
          text={blob!.name}
          href={blob!.url}
          onRemove={
            !props.locked
              ? async () => {
                  const response = await AutomationFileDAO.removeBlobFromInputFile(
                    props.runId!,
                    props.automationInputFile.automationInputFileConfig.name
                  );
                  props.updateAutomationInputFile(response.automationInputFile);
                }
              : null
          }
        />
      ) : null}
    </>
  );
}

interface RunAutomationInputFileViewerProps {
  runId: string | null;
  automationInputFile: AutomationInputFileGraphQL;
  locked: boolean;
  hasOutputFileConfig: boolean;
  isConnectionRun: boolean;
  handleRefreshVersionedData: () => Promise<void>;
  viewer?: boolean;
  connectionId: string | null;
  instrumentServiceDetails?: InstrumentServiceDetails | null;
}

interface QueryProps {
  isStale: boolean;
  onUpdate: (
    automationInputFile: AutomationInputFileGraphQL,
    automationOutputFile: AutomationOutputFileGraphQL | null
  ) => Promise<void>;
}

export const REFETCH_STATUS_INTERVAL_MS = 60000; // 1 minute

/**
 * This component represents an AutomationInputFile in a NoteLinkedAssayData.
 *
 * What is rendered is a function of the status of the AutomationInputFile.
 */
function RunAutomationInputFileViewer(
  props: RunAutomationInputFileViewerProps & QueryProps & ConnectedProps
): JSX.Element | null {
  const {automationInputFile} = props;

  return (
    <Row
      className="u-mini-spacing-top"
      bufferRowReset={true}
      dataTestAttrs={{component: 'RunAutomationInputFileViewer', element: 'root'}}
    >
      <Col md={3}>
        <span className="fieldLabel-sublabel">Input</span>
      </Col>
      <Col md={9}>
        {props.inputFileStatus === AutomationFileStatusGraphQL.succeeded ? (
          <SucceededAutomationInputFile
            automationInputFile={automationInputFile}
            isStale={props.isStale}
            runId={props.runId}
            locked={props.locked}
            handleRefreshVersionedData={props.handleRefreshVersionedData}
            updateAutomationInputFile={(updatedInputFile) => props.onUpdate(updatedInputFile, null)}
          />
        ) : (
          <GenerateAutomationInputFileButton
            runId={props.runId}
            automationInputFile={automationInputFile}
            isConnectionRun={props.isConnectionRun}
            isStale={props.isStale}
            disabled={props.locked || props.runId == null}
            hasOutputFileConfig={props.hasOutputFileConfig}
            handleRefreshVersionedData={props.handleRefreshVersionedData}
            inputFileStatus={props.inputFileStatus}
            updateAutomationFiles={props.onUpdate}
            connectionId={props.connectionId}
            instrumentServiceDetails={props.instrumentServiceDetails}
          />
        )}
        {props.inputFileStatus === AutomationFileStatusGraphQL.failed && (
          <>
            <FailedRunAutomationFileButton
              automationFile={automationInputFile}
              isStale={props.isStale}
              runId={props.runId}
              configName={automationInputFile.automationInputFileConfig.name}
              locked={props.locked}
              handleRefreshVersionedData={props.handleRefreshVersionedData}
              updateAutomationOutputFiles={noop}
              updateAutomationInputFile={(updatedFile) => props.onUpdate(updatedFile, null)}
              previousFileNames={new Set()}
            />
            {automationInputFile.status === AutomationFileStatusGraphQL.failed &&
              automationInputFile.blob && (
                <Chip
                  className="chip-container"
                  icon="paperclip"
                  size={ChipSize.MINI}
                  text={automationInputFile.blob.name}
                  href={automationInputFile.blob.url}
                  onRemove={
                    !props.locked
                      ? async () => {
                          const response = await AutomationFileDAO.removeBlobFromInputFile(
                            props.runId!,
                            automationInputFile.automationInputFileConfig.name
                          );
                          await props.onUpdate(response.automationInputFile, null);
                        }
                      : null
                  }
                />
              )}
          </>
        )}
      </Col>
    </Row>
  );
}

interface ConnectedProps {
  inputFileStatus: AutomationFileStatusGraphQL | null;
}

class RunAutomationInputFileViewerConnector extends DataConnector<
  RunAutomationInputFileViewerProps & QueryProps,
  ConnectedProps
> {
  enhanceProps(): ConnectedProps {
    return {
      inputFileStatus: this.props.automationInputFile.id
        ? this.reduxState().ui.automationFileStatus[this.props.automationInputFile.id]
        : null,
    };
  }
}

const ConnectedRunAutomationInputFileViewer = RunAutomationInputFileViewerConnector.connect(
  RunAutomationInputFileViewer
);

export default function QueryingRunAutomationInputFileViewer(
  props: RunAutomationInputFileViewerProps & {
    updateAutomationInputFile: (
      automationInputFile: AutomationInputFileGraphQL,
      automationOutputFile: AutomationOutputFileGraphQL | null
    ) => void;
    refreshCounter?: number;
  }
): JSX.Element {
  const automationInputFileId = props.automationInputFile.id;
  const result = useSkippableBenchlingQuery<
    RunAutomationInputFileViewerQuery,
    RunAutomationInputFileViewerQueryVariables
  >(RUN_AUTOMATION_INPUT_FILE_VIEWER_QUERY, {
    variables: {
      automationInputFileId,
    },
    skip: automationInputFileId == null || automationInputFileId === '',
  });
  const liveInputFile =
    result.type === QueryResultType.SUCCESS && !isInaccessible(result.data.automationInputFile)
      ? result.data.automationInputFile
      : null;
  const liveStatus = liveInputFile ? getStatusForInputFileViewer(liveInputFile) : undefined;
  const persistedStatus = getStatusForInputFileViewer(props.automationInputFile);
  useEffect(() => {
    if (liveStatus) {
      setAutomationFileStatus(automationInputFileId, liveStatus);
    } else {
      setAutomationFileStatus(automationInputFileId, persistedStatus);
    }
  }, [automationInputFileId, liveStatus, persistedStatus]);

  const refetchLiveInputFile = useCallback(async () => {
    if (!(automationInputFileId == null || automationInputFileId === '')) {
      await result.refetch({automationInputFileId});
    }
  }, [result, automationInputFileId]);

  // Keep a ref to the initial/last-handled tick of the refresh counter since we cannot have a useEffect
  // that depends purely on refreshCounter but not refetchLiveInputFile
  const lastRefreshCounter = useRef(0);
  useEffect(() => {
    // Refetch live status periodically, and also immediately when Run data is refreshed, so it does not fall
    // behind the persisted status.
    if (props.refreshCounter && props.refreshCounter > lastRefreshCounter.current) {
      lastRefreshCounter.current = props.refreshCounter;
      refetchLiveInputFile();
    }
    const intervalId = setInterval(() => {
      refetchLiveInputFile();
    }, REFETCH_STATUS_INTERVAL_MS);
    return () => {
      clearInterval(intervalId);
    };
  }, [refetchLiveInputFile, props.refreshCounter]);

  return (
    <ConnectedRunAutomationInputFileViewer
      {...props}
      isStale={liveInputFile ? liveStatus !== persistedStatus : false}
      onUpdate={async (newInputFile, newOutputFile) => {
        await refetchLiveInputFile();
        props.updateAutomationInputFile(newInputFile, newOutputFile);
      }}
    />
  );
}

const styles = StyleSheet.create({
  buttonIcon: {
    color: Colors.GREEN,
  },
});
